import React from "react";
import { cn } from "@/lib/utils";
import { Button, type ButtonProps } from "@/styles/ui/button";
import { Loader2 } from "lucide-react";

interface SubmitButtonProps extends Omit<ButtonProps, "children"> {
  loading?: boolean;
  text: string;
}

export const SubmitButton: React.FC<SubmitButtonProps> = ({
  loading = false,
  disabled = false,
  text,
  type = "submit",
  variant,
  size,
  className,
  ...props
}) => {
  const isDisabled = loading || disabled;

  return (
    <Button
      type={type}
      disabled={isDisabled}
      variant={"ghost"}
      size={size || "default"}
      className={cn(
        // Base styles
        "w-full h-9 rounded-md !bg-[#171717] !text-[#fafafa]",
        "shadow-sm",
        "transition-all duration-200",
        "relative overflow-hidden",

        // Disabled state (not loading)
        isDisabled && [
          "disabled:!bg-[#171717]/50 disabled:!text-[#fafafa]",
          "disabled:cursor-not-allowed",
          "disabled:hover:!bg-neutral-200", // Prevent hover effect
        ],
        loading && [
          "!bg-[#171717]/50 !text-[#fafafa]",
          "cursor-wait",
          "hover:!bg-neutral-200", // Prevent hover effect
        ],
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          "inline-flex items-center justify-center gap-2",
          "transition-opacity duration-200",
          loading && "opacity-80",
        )}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        <span className="whitespace-nowrap label-1-normal-medium">{text}</span>
      </span>
    </Button>
  );
};
